import type { PaymentOptionsRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { getSilencedCarManagers, useAuthContext } from 'noddi-web';
import { toOrganizationBookingPeople } from '../utils/organizationBookingAssignee';
import { organizationCarBooking, paymentOptionsWithOrganizationInvoice } from '../utils/organizationCarBooking';
import { deriveOrganizationCheckout } from '../utils/organizationCheckout';
import { useBookingSmsOptIns } from './useBookingSmsOptIns';
import { useOrganizationBookingAssignee } from './useOrganizationBookingAssignee';
import { useStationaryPaymentChoice } from './useStationaryPaymentChoice';

type Args = {
  isLoggedIn: boolean;
  /** Group the membership checks run on: the customer's own, or the organization of theirs they picked. */
  memberUserGroupId: number | null;
  /** The customer's own organization this booking is made as. Wins over the company that owns the car. */
  memberOrganization: { userGroupId: number; name: string } | null;
  licensePlateNumbers: string[];
  paymentOptions: PaymentOptionsRecord | undefined;
  /** Names an invoice falls back to when neither payer nor organization names one. */
  fallbackNames: Array<string | null | undefined>;
};

/**
 * The organization side of a booking summary, shared by home delivery and the
 * service center: payment choice, booking party and invoice naming, the car's
 * drivers (who the booking can be assigned to, and who has booking SMS off),
 * and the per-booking SMS opt-ins. A company car can be invoiced to its owner;
 * paying now stays on the customer.
 */
export function useOrganizationCheckout(args: Args) {
  const { userData } = useAuthContext();
  const organizationUserGroupId = args.memberOrganization?.userGroupId ?? null;
  const smsOptIns = useBookingSmsOptIns(organizationUserGroupId);

  // Members of the customer's own organization. A non-member cannot read this
  // list, and a disabled query must not hold the summary open.
  const carDefaultsEnabled = organizationUserGroupId != null && args.licensePlateNumbers.length > 0;
  const {
    data: carDefaults,
    isLoading: isCarDefaultsLoading,
    error: carDefaultsError,
    isFetching: isCarDefaultsFetching,
    refetch: refetchCarDefaults
  } = noddiAsync.useGet({
    type: URLKeys.getCarNotificationDefaults,
    input: { userGroupId: organizationUserGroupId ?? 0, licensePlateNumbers: args.licensePlateNumbers },
    queryConfig: { enabled: carDefaultsEnabled }
  });
  const carManagers = (carDefaults ?? []).filter((member) => member.isCarManager);

  const organizationCar = organizationCarBooking({
    ownsBookingOrganization: args.memberOrganization != null,
    nonMemberBookableOrganization: args.paymentOptions?.nonMemberBookableOrganization
  });
  const payment = useStationaryPaymentChoice({
    isOrganization: args.memberOrganization != null,
    paymentOptions: paymentOptionsWithOrganizationInvoice(args.paymentOptions?.paymentOptions, organizationCar),
    keepPayBeforeDefault: organizationCar != null
  });
  const checkout = deriveOrganizationCheckout({
    isLoggedIn: args.isLoggedIn,
    memberUserGroupId: args.memberUserGroupId,
    memberOrganization: args.memberOrganization,
    organizationCar,
    paymentOptions: args.paymentOptions,
    isInvoice: payment.isInvoice,
    showPaymentChoice: payment.showPaymentChoice,
    memberDrivers: toOrganizationBookingPeople(carManagers),
    fallbackNames: args.fallbackNames
  });
  const bookingAssignee = useOrganizationBookingAssignee(checkout.organizationDrivers);

  const bookerUserId = userData?.user.id ?? null;
  const isBookerOptedIn = bookerUserId != null && smsOptIns.userIds.has(bookerUserId);

  return {
    ...payment,
    ...checkout,
    organizationUserGroupId,
    smsOptIns,
    isBookerOptedIn,
    setBookerOptIn: (optedIn: boolean) => {
      if (bookerUserId != null) {
        smsOptIns.setOptIn(bookerUserId, optedIn);
      }
    },
    /** False when an organization booker has not opted in: only the car's drivers are texted. */
    isBookerTexted: organizationUserGroupId == null || isBookerOptedIn,
    carManagers,
    silencedCarManagers: getSilencedCarManagers(carManagers),
    bookingAssignee,
    isCarDefaultsLoading,
    carDefaultsError,
    isCarDefaultsFetching,
    refetchCarDefaults
  };
}
